import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import FormInput from "../components/FormInput";
import PrimaryButton from "../components/PrimaryButton";

const AUTH_TOKEN_STORAGE_KEY = "authToken";
const AUTH_ROLE_STORAGE_KEY = "authRole";
const normalizeRole = (role) => {
  const cleaned = String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "_");

  if (cleaned === "SUPERADMIN") {
    return "SUPER_ADMIN";
  }

  return cleaned;
};

const INITIAL_TANNERY_FORM = {
  name: "",
  address: "",
  phone: "",
  location_link: "",
};

const getBaseApiUrl = () => {
  const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
  if (!baseApiUrl) {
    throw new Error("REACT_APP_BASE_API_URL is not configured.");
  }
  return baseApiUrl.replace(/\/+$/, "");
};

const buildCreateTanneryPayload = (formValues) => ({
  name: formValues.name.trim(),
  address: formValues.address.trim(),
  phone: formValues.phone.trim(),
  location_link: formValues.location_link.trim(),
});

const validateTanneryForm = (form) => {
  const errors = {};
  const name = form.name.trim();
  if (!name) {
    errors.name = "Tannery name is required.";
  } else if (name.length < 2 || name.length > 150) {
    errors.name = "Tannery name must be 2–150 characters.";
  }

  const address = form.address.trim();
  if (!address) {
    errors.address = "Address is required.";
  } else if (address.length < 2 || address.length > 255) {
    errors.address = "Address must be 2–255 characters.";
  }

  const phone = form.phone.trim();
  if (!phone) {
    errors.phone = "Phone is required.";
  } else if (phone.length < 5 || phone.length > 30) {
    errors.phone = "Phone must be 5–30 characters.";
  }

  const link = form.location_link.trim();
  if (!link) {
    errors.location_link = "Location link (map / pin URL) is required.";
  } else if (link.length < 4 || link.length > 255) {
    errors.location_link = "Location link must be 4–255 characters.";
  }

  return errors;
};

function CreateTanneryPage() {
  const navigate = useNavigate();
  const isSuperAdmin =
    normalizeRole(localStorage.getItem(AUTH_ROLE_STORAGE_KEY)) === "SUPER_ADMIN";
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreatingTannery, setIsCreatingTannery] = useState(false);
  const [tanneryForm, setTanneryForm] = useState(INITIAL_TANNERY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [createMessage, setCreateMessage] = useState("");
  const [createError, setCreateError] = useState("");
  const [tanneries, setTanneries] = useState([]);
  const [isTanneriesLoading, setIsTanneriesLoading] = useState(false);
  const [tanneriesError, setTanneriesError] = useState("");

  const parseApiPayload = async (response) => {
    const responseText = await response.text();
    if (!responseText) {
      return null;
    }

    try {
      return JSON.parse(responseText);
    } catch (error) {
      return responseText;
    }
  };

  const fetchTanneries = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsTanneriesLoading(true);
      setTanneriesError("");

      const response = await fetch(`${getBaseApiUrl()}/super-admin/tanneries`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await parseApiPayload(response);
      if (!response.ok) {
        throw new Error(
          (payload && typeof payload === "object" && payload.message) ||
            "Unable to load tanneries right now."
        );
      }

      const tanneryList = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];

      setTanneries(
        tanneryList.map((tannery, index) => ({
          id: tannery.id ?? tannery.tannery_id ?? `row-${index}`,
          displayName: tannery.name ?? tannery.tannery_name ?? "-",
          adminUsername: tannery.username ?? tannery.admin_username ?? "",
          address: tannery.address ?? "-",
          phone: tannery.phone ?? "-",
          location_link: tannery.location_link ?? "",
        }))
      );
    } catch (error) {
      setTanneriesError(error.message || "Unable to load tanneries right now.");
    } finally {
      setIsTanneriesLoading(false);
    }
  }, [navigate]);

  const openCreateModal = () => {
    setTanneryForm(INITIAL_TANNERY_FORM);
    setFieldErrors({});
    setCreateError("");
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setCreateError("");
    setFieldErrors({});
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setTanneryForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    setCreateError("");
  };

  const handleCreateTannery = async (event) => {
    event.preventDefault();
    const validationErrors = validateTanneryForm(tanneryForm);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      setCreateError("Session expired. Please login again.");
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsCreatingTannery(true);
      setCreateError("");

      const payload = buildCreateTanneryPayload(tanneryForm);
      const response = await fetch(`${getBaseApiUrl()}/super-admin/tanneries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      let responsePayload = null;
      let message = "Tannery created successfully.";

      if (responseText) {
        try {
          responsePayload = JSON.parse(responseText);
          const data =
            responsePayload?.data && typeof responsePayload.data === "object"
              ? responsePayload.data
              : responsePayload;
          const tanneryId = data?.tannery_id ?? responsePayload?.tannery_id;
          const tanneryName = data?.tannery_name ?? responsePayload?.tannery_name;
          if (tanneryId != null && tanneryName) {
            message = `Tannery "${tanneryName}" created (ID: ${tanneryId}). Create a Tannery Admin from Create Users if needed.`;
          } else if (typeof responsePayload?.message === "string" && responsePayload.message.trim()) {
            message = responsePayload.message;
          }
        } catch (parseError) {
          message = responseText;
        }
      }

      if (!response.ok) {
        throw new Error(message || "Unable to create tannery right now.");
      }

      setCreateMessage(message);
      setTanneryForm(INITIAL_TANNERY_FORM);
      setFieldErrors({});
      closeCreateModal();
      fetchTanneries();
    } catch (error) {
      setCreateError(error.message || "Unable to create tannery right now.");
    } finally {
      setIsCreatingTannery(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }
    fetchTanneries();
  }, [fetchTanneries, isSuperAdmin]);

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <section className="dashboard-card" id="create-tannery">
      <div className="dashboard-users-list-header">
        <h2>Tanneries</h2>
        <div className="dashboard-header-actions">
          <PrimaryButton type="button" onClick={openCreateModal}>
            New tannery
          </PrimaryButton>
          <PrimaryButton type="button" onClick={fetchTanneries} disabled={isTanneriesLoading}>
            {isTanneriesLoading ? "Refreshing..." : "Refresh"}
          </PrimaryButton>
        </div>
      </div>
      <p className="dashboard-card-subtitle">
        Registered tanneries. Admin username appears when a Tannery Admin exists. Assign admins from
        Create Users.
      </p>

      {createMessage ? (
        <p className="dashboard-success-message" role="status">
          {createMessage}
        </p>
      ) : null}

      {tanneriesError ? (
        <p className="dashboard-error-message" role="alert">
          {tanneriesError}
        </p>
      ) : null}

      {isTanneriesLoading ? <p>Loading tanneries...</p> : null}
      {!isTanneriesLoading && tanneries.length === 0 ? <p>No tanneries found.</p> : null}

      {!isTanneriesLoading && tanneries.length > 0 ? (
        <div className="dashboard-users-table-wrapper">
          <table className="dashboard-users-table">
            <thead>
              <tr>
                <th scope="col">Tannery name</th>
                <th scope="col">Admin username</th>
                <th scope="col">Address</th>
                <th scope="col">Phone</th>
                <th scope="col">Location link</th>
              </tr>
            </thead>
            <tbody>
              {tanneries.map((tannery, index) => (
                <tr key={`${tannery.id}-${index}`}>
                  <td>{tannery.displayName}</td>
                  <td>{tannery.adminUsername ? tannery.adminUsername : "—"}</td>
                  <td>{tannery.address}</td>
                  <td>{tannery.phone}</td>
                  <td>
                    {tannery.location_link ? (
                      <a href={tannery.location_link} target="_blank" rel="noreferrer">
                        Open
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {isCreateModalOpen ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-tannery-modal-title"
          onClick={closeCreateModal}
        >
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2 id="create-tannery-modal-title">Create new tannery</h2>
              <button type="button" className="modal-close-btn" onClick={closeCreateModal}>
                ×
              </button>
            </div>

            {createError ? (
              <p className="dashboard-error-message create-tannery-modal-error" role="alert">
                {createError}
              </p>
            ) : null}

            <form onSubmit={handleCreateTannery} noValidate>
              <div className="modal-form">
                <FormInput
                  id="create-tannery-name"
                  name="name"
                  label="Tannery name"
                  value={tanneryForm.name}
                  onChange={handleChange}
                  placeholder="Enter Tannery Name."
                  error={fieldErrors.name}
                  required
                />
                <FormInput
                  id="create-tannery-address"
                  name="address"
                  label="Address"
                  value={tanneryForm.address}
                  onChange={handleChange}
                  placeholder="Enter tannery address"
                  error={fieldErrors.address}
                  required
                />
                <FormInput
                  id="create-tannery-phone"
                  name="phone"
                  label="Phone"
                  value={tanneryForm.phone}
                  onChange={handleChange}
                  placeholder="Enter Tannery Phone Number"
                  error={fieldErrors.phone}
                  required
                />
                <FormInput
                  id="create-tannery-location"
                  name="location_link"
                  label="Location link"
                  value={tanneryForm.location_link}
                  onChange={handleChange}
                  placeholder="Enter Pin Location."
                  error={fieldErrors.location_link}
                  required
                />
              </div>
              <div className="modal-footer">
                <PrimaryButton type="button" onClick={closeCreateModal} disabled={isCreatingTannery}>
                  Cancel
                </PrimaryButton>
                <PrimaryButton type="submit" disabled={isCreatingTannery}>
                  {isCreatingTannery ? "Creating..." : "Create tannery"}
                </PrimaryButton>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default CreateTanneryPage;
